import apiClient from '@services/apiClient';
import { useQuery } from '@tanstack/react-query';
import type {
	ClassifierResponse,
	FXCurrencyConfig,
	FXCurrencyFormConfigEntry,
	FXFormFieldConfig,
	FXFormSectionConfig,
} from 'types/operations.ts';

import { useAuth } from './useAuth';

const normalizeSection = (section: FXFormSectionConfig): FXFormSectionConfig => ({
	...section,
	fields: Object.fromEntries(
		Object.entries(section.fields as Record<string, FXFormFieldConfig>).sort(
			([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0),
		),
	),
});

const selectClassifiers = (
	data: Record<string, FXCurrencyFormConfigEntry>,
): FXCurrencyFormConfigEntry[] =>
	Object.values(data).map((entry) => ({
		...entry,
		config: {
			general: normalizeSection(entry.config.general),
			...(!entry.config.local.disabled && {
				local: normalizeSection(entry.config.local),
			}),
			...(!entry.config.swift.disabled && {
				swift: normalizeSection(entry.config.swift),
			}),
		} as FXCurrencyConfig,
	}));

const fetchClassifiers = async (): Promise<Record<string, FXCurrencyFormConfigEntry>> => {
	const response = await apiClient.get<ClassifierResponse>('/classifiers');

	return response.data.fxCurrencyFormConfig;
};

export const useClassifiers = () => {
	const { session } = useAuth();

	return useQuery({
		queryKey: ['classifiers'],
		queryFn: fetchClassifiers,
		enabled: !!session?.tokenized,
		staleTime: Infinity,
		gcTime: Infinity,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		select: selectClassifiers,
	});
};
